// Report card canvas — faithful to RMS: %-positioned layers in a 794×1123
// (A4 @96dpi) base scaled by one transform; pt/mm fonts. Supports multi-select
// (shift-click), anchor-tree drag cascade (drag a parent → descendants/folder
// members translate with it) and anchor-projected resize (children translate to
// keep their spot on the anchor's new box without scaling).

import { useRef, useEffect } from "react"
import { type Layer, CANVAS_W, CANVAS_H, DISPLAY_W } from "./types"
import { renderLayer } from "./LayerPreview"
import { RmsLayerBody, isRmsType, type RmsTpl } from "./rmsRender"

type Tpl = RmsTpl & { columns?: Record<string, boolean>; signatures?: string[] }

type Props = {
  layers: Layer[]
  selectedId: string | null
  selectedIds: Set<string>
  assetUrls: Record<string, string>
  background: string
  tpl: Tpl
  zoomPct?: number
  showGrid?: boolean
  onSelect: (id: string | null, additive?: boolean) => void
  onLayersChange: (layers: Layer[]) => void
  onNeedUrl?: (key: string) => void
}

const num = (v: any, f: number) => (Number.isFinite(v) ? Number(v) : f)
const pctRect = (l: any) => ({
  leftPct: num(l.leftPct, (l.x / CANVAS_W) * 100),
  topPct: num(l.topPct, (l.y / CANVAS_H) * 100),
  widthPct: num(l.widthPct, (l.w / CANVAS_W) * 100),
  heightPct: num(l.heightPct, (l.h / CANVAS_H) * 100),
})

// Every layer anchored (directly or transitively) to `rootId`.
const descendantsOf = (layers: Layer[], rootId: string): Layer[] => {
  const out: Layer[] = []
  const queue = [rootId]
  const seen = new Set([rootId])
  while (queue.length) {
    const cur = queue.shift()!
    for (const l of layers) {
      if ((l as any).anchorParentId === cur && !seen.has(l.id)) {
        seen.add(l.id); out.push(l); queue.push(l.id)
      }
    }
  }
  return out
}

export const Canvas = ({ layers, selectedId, selectedIds, assetUrls, background, tpl, zoomPct = 100, showGrid = false, onSelect, onLayersChange, onNeedUrl }: Props) => {
  const baseScale = DISPLAY_W / CANVAS_W
  const scale = baseScale * (zoomPct / 100)
  const displayW = CANVAS_W * scale
  const displayH = CANVAS_H * scale

  const layersRef = useRef(layers)
  useEffect(() => { layersRef.current = layers }, [layers])

  const drag = useRef<{
    mode: "drag" | "resize"
    sx: number; sy: number
    starts: Record<string, { leftPct: number; topPct: number; widthPct: number; heightPct: number }>
    rootId: string
    rootStart: { leftPct: number; topPct: number; widthPct: number; heightPct: number }
    childUV: Record<string, { u: number; v: number; w: number; h: number }>
  } | null>(null)

  // Build the set of layers that move together when `layerId` is dragged.
  const buildDragSet = (layerId: string): string[] => {
    const ls = layersRef.current
    const base = new Set<string>()
    // Multi-selection drags together; otherwise just this layer.
    if (selectedIds.size > 1 && selectedIds.has(layerId)) selectedIds.forEach(id => base.add(id))
    else base.add(layerId)
    // Folder anchor → its folder members.
    for (const id of Array.from(base)) {
      const l = ls.find(x => x.id === id) as any
      if (l?.isFolderAnchor && l.groupId) ls.forEach(m => { if ((m as any).groupId === l.groupId && m.type !== "folder") base.add(m.id) })
    }
    // Anchor-tree descendants.
    for (const id of Array.from(base)) descendantsOf(ls, id).forEach(d => base.add(d.id))
    return Array.from(base).filter(id => {
      const l = ls.find(x => x.id === id)
      return l && l.type !== "folder" && !l.locked && !l._inherited
    })
  }

  const startDrag = (e: React.MouseEvent, layerId: string, mode: "drag" | "resize") => {
    e.stopPropagation()
    const ls = layersRef.current
    const root = ls.find(l => l.id === layerId)!
    const rootStart = pctRect(root)
    const starts: Record<string, any> = {}
    const childUV: Record<string, any> = {}
    if (mode === "drag") {
      for (const id of buildDragSet(layerId)) starts[id] = pctRect(ls.find(l => l.id === id)!)
    } else {
      starts[layerId] = rootStart
      // Resize projects folder members + anchor children: capture their
      // centre offset (U,V) from the anchor's centre, normalised by anchor size.
      const acx = rootStart.leftPct + rootStart.widthPct / 2
      const acy = rootStart.topPct + rootStart.heightPct / 2
      const kids = ls.filter(l => (l as any).anchorParentId === layerId || ((root as any).isFolderAnchor && (l as any).groupId === (root as any).groupId && l.id !== layerId && l.type !== "folder"))
      for (const k of kids) {
        const r = pctRect(k)
        childUV[k.id] = { u: (r.leftPct + r.widthPct / 2 - acx) / rootStart.widthPct, v: (r.topPct + r.heightPct / 2 - acy) / rootStart.heightPct, w: r.widthPct, h: r.heightPct }
      }
    }
    drag.current = { mode, sx: e.clientX, sy: e.clientY, starts, rootId: layerId, rootStart, childUV }
  }

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = drag.current
      if (!d) return
      const dxPct = ((e.clientX - d.sx) / displayW) * 100
      const dyPct = ((e.clientY - d.sy) / displayH) * 100
      const patch: Record<string, Partial<Layer>> = {}
      if (d.mode === "drag") {
        for (const [id, s] of Object.entries(d.starts)) {
          const leftPct = s.leftPct + dxPct, topPct = s.topPct + dyPct
          patch[id] = { leftPct, topPct, x: Math.round((leftPct / 100) * CANVAS_W), y: Math.round((topPct / 100) * CANVAS_H) } as any
        }
      } else {
        const s = d.rootStart
        const widthPct = Math.max(2, s.widthPct + dxPct)
        const heightPct = Math.max(1, s.heightPct + dyPct)
        patch[d.rootId] = { widthPct, heightPct, w: Math.round((widthPct / 100) * CANVAS_W), h: Math.round((heightPct / 100) * CANVAS_H) } as any
        // Project children onto the anchor's new box (translate, don't scale).
        const ncx = s.leftPct + widthPct / 2, ncy = s.topPct + heightPct / 2
        for (const [id, uv] of Object.entries(d.childUV)) {
          const cx = ncx + uv.u * widthPct, cy = ncy + uv.v * heightPct
          const leftPct = cx - uv.w / 2, topPct = cy - uv.h / 2
          patch[id] = { leftPct, topPct, x: Math.round((leftPct / 100) * CANVAS_W), y: Math.round((topPct / 100) * CANVAS_H) } as any
        }
      }
      onLayersChange(layersRef.current.map(l => patch[l.id] ? { ...l, ...patch[l.id] } : l))
    }
    const onUp = () => { drag.current = null }
    window.addEventListener("mousemove", onMove)
    window.addEventListener("mouseup", onUp)
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp) }
  }, [displayW, displayH, onLayersChange])

  return (
    <div style={{ flex: 1, overflow: "auto", background: "#525659", display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "28px 20px", minWidth: 0 }}>
      <div style={{ width: displayW, height: displayH, position: "relative", flexShrink: 0 }}>
        <div
          onMouseDown={e => { if (e.currentTarget === e.target) onSelect(null) }}
          style={{
            width: CANVAS_W, height: CANVAS_H, position: "absolute", top: 0, left: 0,
            background, boxShadow: "0 2px 16px rgba(0,0,0,0.4)", overflow: "hidden",
            transform: `scale(${scale})`, transformOrigin: "top left", userSelect: "none",
          }}
        >
          {layers.map(layer => {
            if (!layer) return null
            if (layer.type === "folder" || (layer as any).hidden) return null
            const sel = selectedId === layer.id || selectedIds.has(layer.id)
            const { leftPct, topPct, widthPct, heightPct } = pctRect(layer)
            const styledTable = layer.type === "rms_subject_table" && (layer as any).variant === "styled"
            // Images with no stored height render at natural aspect (height:auto), like RMS.
            const autoHeight = styledTable || !Number.isFinite((layer as any).heightPct)
            const editable = !layer.locked && !layer._inherited
            return (
              <div
                key={layer.id}
                onMouseDown={e => {
                  e.stopPropagation()
                  onSelect(layer.id, e.shiftKey)
                  if (!editable || e.shiftKey) return
                  startDrag(e, layer.id, "drag")
                }}
                style={{
                  position: "absolute",
                  top: `${topPct}%`, left: `${leftPct}%`, width: `${widthPct}%`,
                  height: autoHeight ? "auto" : `${heightPct}%`,
                  transform: layer.rotation ? `rotate(${layer.rotation}deg)` : undefined,
                  transformOrigin: "center center",
                  opacity: layer.opacity ?? 1,
                  cursor: editable ? "move" : "default",
                  outline: sel ? `2px solid ${layer._inherited ? "#a16207" : "#2563eb"}` : "none",
                  outlineOffset: -1,
                  zIndex: layers.indexOf(layer) + 1,
                }}
              >
                {isRmsType(layer.type)
                  ? <RmsLayerBody layer={layer as any} assetUrls={assetUrls} tpl={tpl} onNeedUrl={onNeedUrl} />
                  : renderLayer(layer, 1, tpl, assetUrls, onNeedUrl)}

                {sel && editable && (
                  <div
                    style={{ position: "absolute", bottom: -5, right: -5, width: 11, height: 11, background: "#2563eb", border: "2px solid #fff", borderRadius: 3, cursor: "nwse-resize", zIndex: 99 }}
                    onMouseDown={e => { e.stopPropagation(); startDrag(e, layer.id, "resize") }}
                  />
                )}
              </div>
            )
          })}

          {showGrid && <Grid />}
        </div>
      </div>
    </div>
  )
}

function Grid() {
  const lines: React.ReactNode[] = []
  for (let p = 5; p < 100; p += 5) {
    const major = p % 10 === 0
    lines.push(<div key={`v${p}`} style={{ position: "absolute", top: 0, bottom: 0, left: `${p}%`, borderLeft: `${major ? "1px" : "0.5px"} ${major ? "solid" : "dashed"} rgba(220,38,38,${major ? 0.45 : 0.25})`, pointerEvents: "none" }} />)
    lines.push(<div key={`h${p}`} style={{ position: "absolute", left: 0, right: 0, top: `${p}%`, borderTop: `${major ? "1px" : "0.5px"} ${major ? "solid" : "dashed"} rgba(220,38,38,${major ? 0.45 : 0.25})`, pointerEvents: "none" }} />)
    if (major) {
      lines.push(<div key={`xl${p}`} style={{ position: "absolute", top: 0, left: `${p}%`, transform: "translateX(-50%)", fontSize: 9, fontFamily: "monospace", color: "#dc2626", background: "rgba(255,255,255,0.85)", padding: "0 2px", lineHeight: 1, pointerEvents: "none" }}>{p}%</div>)
      lines.push(<div key={`yl${p}`} style={{ position: "absolute", left: 0, top: `${p}%`, transform: "translateY(-50%)", fontSize: 9, fontFamily: "monospace", color: "#dc2626", background: "rgba(255,255,255,0.85)", padding: "0 2px", lineHeight: 1, pointerEvents: "none" }}>{p}%</div>)
    }
  }
  return <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 9999 }}>{lines}</div>
}

export default Canvas
